import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../ui/dropdown-menu';
import {
  Plus, ChevronRight, ChevronDown, Edit, Layers, Trash2, MoreVertical, Eye, EyeOff, UserPlus, X, Shield, ShieldCheck, Clock, Sparkles, Building
} from 'lucide-react';
import { BlurImage } from '../BlurImage';
import { toast } from 'sonner';
import { BookableFormModal } from '../BookableFormModal';

const bookableTypeLabels = {
  meeting_room: 'Meeting Room',
  conference_room: 'Conference Room',
  day_pass: 'Day Pass',
  dedicated_desk: 'Dedicated Desk',
  event_space: 'Event Space',
  virtual_office: 'Virtual Office'
};

const bookableRateUnits = {
  meeting_room: '/hr',
  conference_room: '/hr',
  day_pass: '/day',
  dedicated_desk: '/month',
  event_space: '/hr',
  virtual_office: '/month'
};

const roleConfig = {
  primary: { label: 'Primary CM', icon: ShieldCheck, color: 'text-green-500 bg-green-500/10 border-green-500/20' },
  secondary: { label: 'Secondary CM', icon: Shield, color: 'text-blue-500 bg-blue-500/10 border-blue-500/20' },
  reliever: { label: 'Reliever 1', icon: Clock, color: 'text-amber-500 bg-amber-500/10 border-amber-500/20' },
  reliever_2: { label: 'Reliever 2', icon: Clock, color: 'text-orange-500 bg-orange-500/10 border-orange-500/20' },
  reliever_3: { label: 'Reliever 3', icon: Clock, color: 'text-yellow-600 bg-yellow-500/10 border-yellow-500/20' },
};

const VENUE_STATUSES = [
  { value: 'active', label: 'Active / Open for Booking', color: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 border' },
  { value: 'inactive', label: 'Inactive / Bookings Unavailable', color: 'bg-slate-400/15 text-slate-500 border-slate-400/30 border' },
  { value: 'sold_out', label: 'Sold Out', color: 'bg-red-500/15 text-red-500 border-red-500/30 border' },
  { value: 'upcoming', label: 'Upcoming', color: 'bg-blue-500/15 text-blue-500 border-blue-500/30 border' },
];

const getStatusConfig = (venue) => {
  const status = venue.venue_status || (venue.is_active ? 'active' : 'inactive');
  return VENUE_STATUSES.find(s => s.value === status) || VENUE_STATUSES[0];
};

export const VenuesTab = ({
  venues, bookables, user, expandedVenue, theme,
  onExpandVenue, onAddVenue, onEditVenue, onAddBookable,
  onUpdateVenueStatus, onToggleBookableActive, onToggleBookableInternal, onDeleteVenue, onDeleteBookable,
  centreAssignments = [], centerManagers = [], api, onRefresh
}) => {
  const { cardBg, cardBorder, innerCardBg } = theme;
  const [assigningSlot, setAssigningSlot] = useState(null); // { venueId, role }
  const [selectedManager, setSelectedManager] = useState('');
  const [saving, setSaving] = useState(false);
  const [bookableFormOpen, setBookableFormOpen] = useState(false);
  const [editingBookable, setEditingBookable] = useState(null);
  const [bookableFormVenueId, setBookableFormVenueId] = useState(null);
  const [parkingVenue, setParkingVenue] = useState(null);
  const [offerVenue, setOfferVenue] = useState(null);
  const [floorsVenue, setFloorsVenue] = useState(null);

  const getBookablesForVenue = (venueId) => bookables.filter(b => b.venue_id === venueId);
  const getAssignmentsForVenue = (venueId) => centreAssignments.filter(a => a.centre_id === venueId);
  const getAssignmentForRole = (venueId, role) => centreAssignments.find(a => a.centre_id === venueId && a.role === role);

  const groupBookablesByType = (items) => {
    return items.reduce((acc, item) => {
      const type = item.bookable_type || 'other';
      if (!acc[type]) acc[type] = [];
      acc[type].push(item);
      return acc;
    }, {});
  };

  const handleAssignSlot = async (venueId, role) => {
    if (!selectedManager) return;
    setSaving(true);
    try {
      await api.post('/centre-assignments', {
        manager_id: selectedManager,
        centre_id: venueId,
        role: role,
      });
      toast.success(`${roleConfig[role].label} assigned successfully`);
      setAssigningSlot(null);
      setSelectedManager('');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign manager');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    try {
      await api.delete(`/centre-assignments/${assignmentId}`);
      toast.success('Assignment removed');
      onRefresh?.();
    } catch (err) {
      toast.error('Failed to remove assignment');
    }
  };

  const RoleSlot = ({ venueId, role }) => {
    const assignment = getAssignmentForRole(venueId, role);
    const rc = roleConfig[role];
    const RIcon = rc.icon;
    const isAssigning = assigningSlot?.venueId === venueId && assigningSlot?.role === role;

    if (isAssigning) {
      return (
        <div className={`p-3 rounded-lg border ${rc.color}`}>
          <div className="flex items-center gap-2 mb-2">
            <RIcon className="w-4 h-4" />
            <span className="text-xs font-semibold">{rc.label}</span>
          </div>
          <div className="flex items-center gap-2">
            <Select value={selectedManager} onValueChange={setSelectedManager}>
              <SelectTrigger className="h-8 text-xs flex-1">
                <SelectValue placeholder="Select manager..." />
              </SelectTrigger>
              <SelectContent>
                {centerManagers.map(m => (
                  <SelectItem key={m.id} value={m.id} className="text-xs">{m.name} ({m.email})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" className="h-8 px-2 text-xs" disabled={!selectedManager || saving} onClick={() => handleAssignSlot(venueId, role)}>
              Save
            </Button>
            <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => { setAssigningSlot(null); setSelectedManager(''); }}>
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>
      );
    }

    if (assignment) {
      return (
        <div className={`p-3 rounded-lg border ${rc.color} flex items-center justify-between`} data-testid={`assignment-${role}-${venueId}`}>
          <div className="flex items-center gap-2 min-w-0">
            <RIcon className="w-4 h-4 shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider opacity-70">{rc.label}</p>
              <p className="text-sm font-medium truncate">{assignment.manager_name}</p>
              <p className="text-xs opacity-60 truncate">{assignment.manager_email}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { setAssigningSlot({ venueId, role }); setSelectedManager(''); }}>
              <Edit className="w-3 h-3" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:bg-destructive/20 hover:text-destructive" onClick={() => handleRemoveAssignment(assignment.id)}>
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>
      );
    }

    return (
      <button
        onClick={() => { setAssigningSlot({ venueId, role }); setSelectedManager(''); }}
        className={`p-3 rounded-lg border border-dashed ${cardBorder} hover:border-primary/40 transition-colors w-full text-left`}
        data-testid={`assign-${role}-${venueId}`}
      >
        <div className="flex items-center gap-2">
          <RIcon className="w-4 h-4 text-muted-foreground" />
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{rc.label}</p>
            <p className="text-xs text-muted-foreground/60">Click to assign</p>
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-6" data-testid="venues-tab">
      <Card className={`${cardBg} ${cardBorder}`}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading">Manage Venues</CardTitle>
          {(user?.role === 'admin' || user?.role === 'hub_manager') && (
            <Button className="bg-primary hover:bg-primary/90" size="sm" onClick={onAddVenue}>
              <Plus className="w-4 h-4 mr-2" /> Add Venue
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {venues.map(venue => {
              const venueBookables = getBookablesForVenue(venue.id);
              const groupedBookables = groupBookablesByType(venueBookables);
              const venueAssignments = getAssignmentsForVenue(venue.id);
              const isExpanded = expandedVenue === venue.id;

              return (
                <div key={venue.id} className={`rounded-xl border ${cardBorder} overflow-hidden ${getStatusConfig(venue).value !== 'active' ? 'opacity-75' : ''}`}>
                  <div
                    className={`flex items-center justify-between p-5 ${innerCardBg} cursor-pointer`}
                    onClick={() => onExpandVenue(isExpanded ? null : venue.id)}
                  >
                    <div className={`flex items-center gap-4 ${getStatusConfig(venue).value === 'inactive' ? 'grayscale' : ''}`}>
                      <div className="w-16 h-16 rounded-xl bg-muted overflow-hidden">
                        <BlurImage
                          src={venue.images?.[0] ? (venue.images[0].startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${venue.images[0]}` : venue.images[0]) : 'https://images.unsplash.com/photo-1497366412874-3415097a27e7?w=200'}
                          thumbnail={venue.thumbnails?.[0]}
                          alt={venue.name}
                          className="w-full h-full"
                        />
                      </div>
                      <div>
                        <h4 className={`font-heading font-semibold text-lg ${getStatusConfig(venue).value === 'inactive' ? 'text-muted-foreground' : ''}`}>{venue.name}</h4>
                        <p className="text-sm text-muted-foreground">{venue.address}, {venue.city}</p>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <Badge className={getStatusConfig(venue).color}>{getStatusConfig(venue).label}</Badge>
                          <Badge variant="outline" className={cardBorder}>{venueBookables.length} Bookables</Badge>
                          <Badge variant="outline" className={cardBorder}>{(venue.floors || []).length} Floor{(venue.floors || []).length === 1 ? '' : 's'} configured</Badge>
                          {venueAssignments.length > 0 && (
                            <Badge variant="outline" className="border-primary/30 text-primary">{venueAssignments.length} Manager{venueAssignments.length > 1 ? 's' : ''}</Badge>
                          )}
                          {venue.offer_enabled && (
                            <Badge className="bg-[#CB6E4A]/15 text-[#CB6E4A] border border-[#CB6E4A]/30" data-testid={`venue-offer-badge-${venue.id}`}>
                              <Sparkles className="w-3 h-3 mr-1" /> Offer Live
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(user?.role === 'admin' || user?.role === 'hub_manager') && (
                        <>
                          <Button variant="outline" size="sm" className={cardBorder} onClick={(e) => { e.stopPropagation(); setBookableFormVenueId(venue.id); setEditingBookable(null); setBookableFormOpen(true); }}>
                            <Plus className="w-4 h-4 mr-1" /> Bookable
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEditVenue(venue); }}>
                                <Edit className="w-4 h-4 mr-2" /> Edit Venue
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setParkingVenue(venue); }} data-testid={`venue-parking-config-${venue.id}`}>
                                <Layers className="w-4 h-4 mr-2" /> Parking Config
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setFloorsVenue(venue); }} data-testid={`venue-floors-config-${venue.id}`}>
                                <Building className="w-4 h-4 mr-2" /> Floors Config
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setOfferVenue(venue); }} data-testid={`venue-offer-config-${venue.id}`}>
                                <Sparkles className="w-4 h-4 mr-2" /> Promo Banner
                              </DropdownMenuItem>
                              {VENUE_STATUSES.map(s => (
                                <DropdownMenuItem
                                  key={s.value}
                                  onClick={(e) => { e.stopPropagation(); onUpdateVenueStatus(venue, s.value); }}
                                  className={getStatusConfig(venue).value === s.value ? 'font-semibold' : ''}
                                >
                                  <span className={`w-2 h-2 rounded-full mr-2 ${s.value === 'active' ? 'bg-emerald-500' : s.value === 'inactive' ? 'bg-slate-400' : s.value === 'sold_out' ? 'bg-red-500' : 'bg-blue-500'}`} />
                                  {s.label}
                                </DropdownMenuItem>
                              ))}
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDeleteVenue(venue); }} className="text-red-500 focus:text-red-500">
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </>
                      )}
                      {isExpanded ? <ChevronDown className="w-5 h-5 text-muted-foreground" /> : <ChevronRight className="w-5 h-5 text-muted-foreground" />}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className={`p-5 border-t ${cardBorder}`}>
                      {/* Centre Manager Assignments - 3 Role Slots */}
                      {(user?.role === 'admin' || user?.role === 'hub_manager') && (
                        <div className="mb-6" data-testid={`venue-assignments-${venue.id}`}>
                          <div className="flex items-center gap-2 mb-3">
                            <UserPlus className="w-4 h-4 text-primary" />
                            <h5 className="font-heading font-semibold">Centre Managers</h5>
                          </div>
                          <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
                            <RoleSlot venueId={venue.id} role="primary" />
                            <RoleSlot venueId={venue.id} role="secondary" />
                            <RoleSlot venueId={venue.id} role="reliever" />
                            <RoleSlot venueId={venue.id} role="reliever_2" />
                            <RoleSlot venueId={venue.id} role="reliever_3" />
                          </div>
                          <div className={`border-b ${cardBorder}`} />
                        </div>
                      )}

                      {/* Bookables Section */}
                      {Object.keys(groupedBookables).length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">No bookables added yet. Click "Add Bookable" to get started.</p>
                      ) : (
                        <div className="space-y-6">
                          {Object.entries(groupedBookables).map(([type, items]) => (
                            <div key={type}>
                              <div className="flex items-center gap-2 mb-3">
                                <Layers className="w-4 h-4 text-primary" />
                                <h5 className="font-heading font-semibold">{bookableTypeLabels[type] || type}</h5>
                                <Badge variant="secondary" className="ml-2">{items.length}</Badge>
                              </div>
                              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 pl-6">
                                {items.map(bookable => {
                                  const extActive = bookable.is_active;
                                  const intActive = bookable.is_active_internal !== false;
                                  let badgeText, badgeVariant;
                                  if (extActive && intActive) { badgeText = 'Public + Internal'; badgeVariant = 'default'; }
                                  else if (!extActive && intActive) { badgeText = 'Internal only'; badgeVariant = 'secondary'; }
                                  else if (extActive && !intActive) { badgeText = 'Public only'; badgeVariant = 'default'; }
                                  else { badgeText = 'Disabled'; badgeVariant = 'secondary'; }
                                  const cardFaded = !extActive && !intActive;
                                  return (
                                  <div key={bookable.id} className={`p-4 rounded-lg ${cardBg} border ${cardBorder} hover:border-primary/30 transition-all ${cardFaded ? 'opacity-50 grayscale bg-muted/20' : ''}`}>
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <h6 className="font-medium">{bookable.name}</h6>
                                        <p className="text-xs text-muted-foreground mt-1">Capacity: {bookable.capacity} &bull; ₹{bookable.hourly_rate}{bookableRateUnits[bookable.bookable_type] || '/hr'}</p>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <Badge variant={badgeVariant} className="text-xs" data-testid={`bookable-status-${bookable.id}`}>{badgeText}</Badge>
                                        <DropdownMenu>
                                          <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0"><MoreVertical className="w-3 h-3" /></Button>
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={() => { setEditingBookable(bookable); setBookableFormOpen(true); }}>
                                              <Edit className="w-4 h-4 mr-2" /> Edit
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => onToggleBookableActive(bookable)} data-testid={`admin-toggle-external-${bookable.id}`}>
                                              {extActive ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                                              {extActive ? 'Disable for External' : 'Enable for External'}
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => onToggleBookableInternal && onToggleBookableInternal(bookable)} data-testid={`admin-toggle-internal-${bookable.id}`}>
                                              {intActive ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                                              {intActive ? 'Disable for Internal' : 'Enable for Internal'}
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => onDeleteBookable(bookable)} className="text-red-500 focus:text-red-500">
                                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                                            </DropdownMenuItem>
                                          </DropdownMenuContent>
                                        </DropdownMenu>
                                      </div>
                                    </div>
                                  </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <BookableFormModal
        open={bookableFormOpen}
        onClose={() => { setBookableFormOpen(false); setEditingBookable(null); }}
        api={api}
        onSuccess={onRefresh}
        venues={venues}
        editBookable={editingBookable}
        preselectedVenueId={bookableFormVenueId}
      />
      <ParkingConfigModal
        venue={parkingVenue}
        onClose={() => setParkingVenue(null)}
        api={api}
        onSaved={onRefresh}
      />
      <OfferConfigModal
        venue={offerVenue}
        onClose={() => setOfferVenue(null)}
        api={api}
        onSaved={onRefresh}
      />
      <FloorsConfigModal
        venue={floorsVenue}
        onClose={() => setFloorsVenue(null)}
        api={api}
        onSaved={onRefresh}
      />
    </div>
  );
};

/* -------------------- Parking Config Modal -------------------- */
export const ParkingConfigModal = ({ venue, onClose, api, onSaved }) => {
  const [fourW, setFourW] = React.useState({ enabled: false, quantity: 0, hourly_rate: 50, daily_rate: 550 });
  const [twoW, setTwoW] = React.useState({ enabled: false, quantity: 0, hourly_rate: 20, daily_rate: 220 });
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!venue) return;
    const p = venue.parking_config || {};
    setFourW({
      enabled: p.four_wheeler?.enabled || false,
      quantity: p.four_wheeler?.quantity || 0,
      hourly_rate: p.four_wheeler?.hourly_rate || 50,
      daily_rate: p.four_wheeler?.daily_rate || 550,
    });
    setTwoW({
      enabled: p.two_wheeler?.enabled || false,
      quantity: p.two_wheeler?.quantity || 0,
      hourly_rate: p.two_wheeler?.hourly_rate || 20,
      daily_rate: p.two_wheeler?.daily_rate || 220,
    });
  }, [venue]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/venues/${venue.id}/parking-config`, {
        four_wheeler: fourW,
        two_wheeler: twoW,
      });
      toast.success(`Parking config saved for ${venue.name}`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save parking config');
    } finally { setSaving(false); }
  };

  if (!venue) return null;
  return (
    <Dialog open={!!venue} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="parking-config-modal">
        <DialogHeader><DialogTitle>Parking Configuration — {venue.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-xs text-muted-foreground">Toggle each parking type, set the available slots and the hourly + day-pass rates. These rates are used when Sales/CM add parking to a walk-in or guest booking at this venue.</p>
          <ParkingSection title="4-Wheeler Parking" state={fourW} setter={setFourW} testPrefix="parking-4w" />
          <ParkingSection title="2-Wheeler Parking" state={twoW} setter={setTwoW} testPrefix="parking-2w" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="parking-config-save">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ParkingSection = ({ title, state, setter, testPrefix }) => (
  <div className={`p-4 rounded-lg border ${state.enabled ? 'border-primary/40 bg-primary/5' : 'border-border'}`}>
    <label className="flex items-center gap-2 cursor-pointer mb-3">
      <input type="checkbox" checked={state.enabled} onChange={(e) => setter({ ...state, enabled: e.target.checked })} data-testid={`${testPrefix}-enabled`} />
      <span className="font-medium">{title}</span>
    </label>
    {state.enabled && (
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Available Slots</Label>
          <Input type="number" min={0} max={999} value={state.quantity} onChange={(e) => setter({ ...state, quantity: Math.max(0, parseInt(e.target.value) || 0) })} data-testid={`${testPrefix}-qty`} />
        </div>
        <div>
          <Label className="text-xs">₹ / Hour</Label>
          <Input type="number" min={0} value={state.hourly_rate} onChange={(e) => setter({ ...state, hourly_rate: Math.max(0, parseFloat(e.target.value) || 0) })} data-testid={`${testPrefix}-hr-rate`} />
        </div>
        <div>
          <Label className="text-xs">₹ / Day Pass</Label>
          <Input type="number" min={0} value={state.daily_rate} onChange={(e) => setter({ ...state, daily_rate: Math.max(0, parseFloat(e.target.value) || 0) })} data-testid={`${testPrefix}-day-rate`} />
        </div>
      </div>
    )}
  </div>
);

/* -------------------- Offer / Promo Banner Modal -------------------- */
const OfferConfigModal = ({ venue, onClose, api, onSaved }) => {
  const [enabled, setEnabled] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [badge, setBadge] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [validUntil, setValidUntil] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!venue) return;
    setEnabled(venue.offer_enabled || false);
    setTitle(venue.offer_title || '');
    setBadge(venue.offer_badge || '');
    setDescription(venue.offer_description || '');
    setValidUntil(venue.offer_valid_until ? venue.offer_valid_until.slice(0, 10) : '');
  }, [venue]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/venues/${venue.id}`, {
        offer_enabled: enabled,
        offer_title: title || null,
        offer_badge: badge || null,
        offer_description: description || null,
        offer_valid_until: validUntil || null,
      });
      toast.success(`Promo banner ${enabled ? 'enabled' : 'disabled'} for ${venue.name}`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save promo banner');
    } finally { setSaving(false); }
  };

  if (!venue) return null;
  return (
    <Dialog open={!!venue} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="offer-config-modal">
        <DialogHeader><DialogTitle>Promo Banner — {venue.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-xs text-muted-foreground">
            When enabled, a promotional banner is shown on this center's public booking page to highlight the offer.
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="offer-enabled" />
            <span className="font-medium">Show promo banner on booking page</span>
          </label>
          <div>
            <Label className="text-xs">Title</Label>
            <Input placeholder="e.g. Clay Days — 50% Off Sale" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="offer-title" />
          </div>
          <div>
            <Label className="text-xs">Badge (short tag)</Label>
            <Input placeholder="e.g. Up to 30% off" value={badge} onChange={(e) => setBadge(e.target.value)} data-testid="offer-badge" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea placeholder="Short line describing the offer" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="offer-description" />
          </div>
          <div>
            <Label className="text-xs">Valid Until</Label>
            <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} data-testid="offer-valid-until" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="offer-config-save">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* -------------------- Floors Config Modal -------------------- */
export const FloorsConfigModal = ({ venue, onClose, api, onSaved }) => {
  const [floors, setFloors] = React.useState([]);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!venue) return;
    const existing = venue.floors || [];
    setFloors(existing.length ? [...existing] : ['']);
  }, [venue]);

  const updateFloor = (idx, value) => setFloors(prev => prev.map((f, i) => (i === idx ? value : f)));
  const removeFloor = (idx) => setFloors(prev => prev.filter((_, i) => i !== idx));
  const moveFloor = (idx, dir) => setFloors(prev => {
    const next = [...prev];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return prev;
    [next[idx], next[target]] = [next[target], next[idx]];
    return next;
  });

  const submit = async () => {
    const cleaned = floors.map(f => f.trim()).filter(Boolean);
    if (new Set(cleaned).size !== cleaned.length) {
      toast.error('Floor names must be unique');
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/venues/${venue.id}/floors`, { floors: cleaned });
      toast.success(`Floors updated for ${venue.name}`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save floors');
    } finally { setSaving(false); }
  };

  if (!venue) return null;
  return (
    <Dialog open={!!venue} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="floors-config-modal">
        <DialogHeader><DialogTitle>Floors — {venue.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-xs text-muted-foreground">
            Configure the floors available at this centre, in display order (e.g. Ground Floor, 1st Floor, 5th Floor).
            Once set, every inventory unit created here must be assigned to one of these floors, and inventory is grouped by floor.
          </p>
          <div className="space-y-2">
            {floors.map((f, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`floor-row-${i}`}>
                <div className="flex flex-col">
                  <button type="button" className="text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={i === 0} onClick={() => moveFloor(i, -1)}>
                    <ChevronRight className="w-3 h-3 -rotate-90" />
                  </button>
                  <button type="button" className="text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={i === floors.length - 1} onClick={() => moveFloor(i, 1)}>
                    <ChevronRight className="w-3 h-3 rotate-90" />
                  </button>
                </div>
                <Input
                  value={f}
                  onChange={(e) => updateFloor(i, e.target.value)}
                  placeholder="e.g. 5th Floor"
                  data-testid={`floor-input-${i}`}
                />
                <Button type="button" size="icon" variant="ghost" onClick={() => removeFloor(i)}>
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => setFloors(prev => [...prev, ''])} data-testid="add-floor-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Floor
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="floors-config-save">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
